import { useEffect, useMemo, useState } from 'react'
import { useStore } from '../store/store'
import {
  DEFAULT_COSTS,
  DEFAULT_STOCK_PARAMS,
  backtestStock,
  computeIndicators,
  expectedValue,
  lastNonNull,
  positionSize,
  riskReward,
  type CostModel,
  type StockWatchAlert,
  type StockBacktestResult,
  type StockStrategyParams,
  type StockStrategyType,
} from '../lib/math'
import type { Asset, Direction, Position, TradeIdea } from '../domain/types'
import { uid } from '../lib/id'
import { fmtCurrency, fmtNumber, fmtPct, fmtPctValue, fmtSigned } from '../lib/format'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  NumberField,
  PageHead,
  Pill,
  RangeField,
  SelectField,
  Stat,
  TextArea,
  Toggle,
  type Tone,
} from '../components/ui'
import { BarChart, CandleChart, LineChart } from '../components/charts'

const trendTone: Record<string, Tone> = { uptrend: 'green', downtrend: 'red', sideways: 'gray' }
const sigTone: Record<string, Tone> = { bullish: 'green', bearish: 'red', neutral: 'gray' }
const signalTone: Record<number, Tone> = { 1: 'green', 0: 'gray', [-1]: 'red' }

const STRATEGY_OPTIONS: Array<{ value: StockStrategyType; label: string }> = [
  { value: 'sma-cross', label: 'Moving-average crossover' },
  { value: 'rsi-reversion', label: 'RSI mean reversion' },
  { value: 'breakout', label: 'Breakout' },
  { value: 'vol-squeeze', label: 'Volatility squeeze' },
  { value: 'gap-fill', label: 'Gap fill' },
  { value: 'momentum', label: 'Momentum' },
]

export function StockAnalyzer() {
  const assets = useStore((s) => s.assets)
  const risk = useStore((s) => s.risk)
  const addTradeIdea = useStore((s) => s.addTradeIdea)
  const addPosition = useStore((s) => s.addPosition)
  const addBacktest = useStore((s) => s.addBacktest)
  const toggleWatch = useStore((s) => s.toggleWatchAsset)
  const watch = useStore((s) => s.watchlistAssetIds)
  const tradeIdeas = useStore((s) => s.tradeIdeas)
  const positions = useStore((s) => s.positions)

  const [symbol, setSymbol] = useState(assets[0]?.id ?? '')
  const asset = assets.find((a) => a.id === symbol) ?? assets[0]
  const ind = useMemo(() => (asset ? computeIndicators(asset.candles) : null), [asset])

  const [direction, setDirection] = useState<Direction>('long')
  const [entry, setEntry] = useState(0)
  const [stop, setStop] = useState(0)
  const [target, setTarget] = useState(0)
  const [modelProb, setModelProb] = useState(0.55)
  const [thesis, setThesis] = useState('')
  const [wizardRiskPct, setWizardRiskPct] = useState(risk.maxRiskPerTradePct)
  const [wizardMaxPct, setWizardMaxPct] = useState(risk.maxSinglePositionPct)
  const [wizardAtrMultiple, setWizardAtrMultiple] = useState(2)

  const [strategyType, setStrategyType] = useState<StockStrategyType>('sma-cross')
  const [fast, setFast] = useState(20)
  const [slow, setSlow] = useState(50)
  const [rsiLow, setRsiLow] = useState(30)
  const [rsiHigh, setRsiHigh] = useState(70)
  const [breakoutLookback, setBreakoutLookback] = useState(20)
  const [breakoutBufferPct, setBreakoutBufferPct] = useState(0.0025)
  const [squeezeThresholdPct, setSqueezeThresholdPct] = useState(0.08)
  const [gapThresholdPct, setGapThresholdPct] = useState(0.02)
  const [useAtrStop, setUseAtrStop] = useState(true)
  const [atrStopMultiple, setAtrStopMultiple] = useState(2)
  const [fee, setFee] = useState(DEFAULT_COSTS.feePct)
  const [slippage, setSlippage] = useState(DEFAULT_COSTS.slippagePct)
  const [spread, setSpread] = useState(DEFAULT_COSTS.spreadPct)
  const [commission, setCommission] = useState(0)
  const [capital, setCapital] = useState(10000)
  const [replayIndex, setReplayIndex] = useState(0)

  useEffect(() => {
    if (!asset || !ind) return
    const price = asset.candles[asset.candles.length - 1]?.close ?? 100
    const atrVal = lastNonNull(ind.atr14) ?? price * 0.02
    setEntry(round2(price))
    setStop(round2(direction === 'long' ? price - atrVal * wizardAtrMultiple : price + atrVal * wizardAtrMultiple))
    setTarget(round2(direction === 'long' ? price + atrVal * 3 : price - atrVal * 3))
  }, [asset, ind, direction, wizardAtrMultiple])

  useEffect(() => {
    if (!asset) return
    setReplayIndex(Math.max(0, asset.candles.length - 1))
  }, [asset])

  if (!asset || !ind) return <div className="empty">No assets loaded. Import OHLCV on the Data Import page.</div>

  const candles = asset.candles
  const last = candles[candles.length - 1]
  const prev = candles[candles.length - 2] ?? last
  const change = prev.close > 0 ? (last.close - prev.close) / prev.close : 0
  const rsiVal = lastNonNull(ind.rsi14)
  const atrVal = lastNonNull(ind.atr14) ?? last.close * 0.02
  const volVal = lastNonNull(ind.volatility)

  const rr = riskReward(entry, stop, target)
  const decimalOdds = rr.valid ? 1 + rr.ratio : 1.01
  const ev = expectedValue({ modelProb, decimalOdds, minEdge: risk.minEdge })
  const sizing = positionSize({
    bankroll: risk.bankroll,
    entryPrice: entry,
    stopPrice: stop,
    riskPctOfBankroll: risk.maxRiskPerTradePct,
    maxPositionPctOfBankroll: risk.maxSinglePositionPct,
  })
  const wizardStop = round2(direction === 'long' ? entry - atrVal * wizardAtrMultiple : entry + atrVal * wizardAtrMultiple)
  const wizardSizing = positionSize({
    bankroll: risk.bankroll,
    entryPrice: entry,
    stopPrice: wizardStop,
    riskPctOfBankroll: wizardRiskPct,
    maxPositionPctOfBankroll: wizardMaxPct,
  })
  const rsiConst = (v: number) => candles.map(() => v)
  const costs: CostModel = { feePct: fee, slippagePct: slippage, spreadPct: spread, commissionPerTrade: commission }
  const strategyParams: StockStrategyParams = {
    ...DEFAULT_STOCK_PARAMS,
    type: strategyType,
    fast,
    slow,
    rsiLow,
    rsiHigh,
    breakoutLookback,
    breakoutBufferPct,
    squeezeThresholdPct,
    gapThresholdPct,
    useAtrStop,
    atrStopMultiple,
  }
  const strategyResult = backtestStock(candles, strategyParams, costs, capital)
  const comparison = buildComparisons(asset, costs, capital)
  const replay = buildReplay(asset, strategyResult, replayIndex)
  const stockLedger = buildStockLedger(asset, tradeIdeas, positions)

  const saveIdea = () => {
    addTradeIdea({
      assetId: asset.id,
      symbol: asset.symbol,
      direction,
      entry,
      stop,
      target,
      thesis: thesis || `${asset.symbol} ${direction} - ${ind.trend.trend}, R:R ${rr.ratio.toFixed(2)}.`,
      modelProb,
      confidence: 0.55,
      dataQuality: ind.riskWarnings.length ? 0.45 : 0.65,
      status: 'watch',
      paper: true,
      tags: [asset.sector ?? 'stock', strategyType],
      correlationGroup: asset.sector ?? 'stock',
    })
    setThesis('')
  }

  const openPaperPosition = () => {
    addPosition({
      kind: 'stock',
      label: `${asset.symbol} ${direction} paper`,
      direction,
      size: wizardSizing.shares,
      entryPrice: entry,
      currentPrice: last.close,
      stake: wizardSizing.positionValue,
      openedAt: new Date().toISOString(),
      status: 'open',
      paper: true,
      correlationGroup: asset.sector ?? 'stock',
    })
  }

  const saveBacktest = () => {
    addBacktest({
      id: uid('bt'),
      name: `${asset.symbol} ${strategyLabel(strategyType)}`,
      createdAt: new Date().toISOString(),
      strategy: strategyType,
      params: { fast, slow, rsiLow, rsiHigh, breakoutLookback, breakoutBufferPct, squeezeThresholdPct, gapThresholdPct, fee, slippage, spread, commission, useAtrStop: useAtrStop ? 1 : 0, atrStopMultiple },
      metrics: strategyResult.metrics,
      inSample: strategyResult.inSample,
      outOfSample: strategyResult.outOfSample,
      equityCurve: strategyResult.equityCurve,
      overfitWarning: strategyResult.overfitWarning,
    })
  }

  return (
    <div className="stack">
      <PageHead title="Stock Analyzer">
        Strategy building, technical analysis, paper trading, and risk-first sizing. Research and paper-trading only.
      </PageHead>

      {ind.riskWarnings.map((warning) => (
        <Banner key={warning.id} tone={warning.severity}>
          {warning.message}
        </Banner>
      ))}

      <div className="row between wrap" style={{ alignItems: 'flex-end' }}>
        <div style={{ minWidth: 240 }}>
          <SelectField
            label="Symbol"
            value={symbol}
            onChange={setSymbol}
            options={assets.map((a) => ({ value: a.id, label: `${a.symbol} - ${a.name}` }))}
          />
        </div>
        <div className="btn-row" style={{ marginBottom: 12 }}>
          <Button size="sm" onClick={() => toggleWatch(asset.id)}>
            {watch.includes(asset.id) ? 'On watchlist' : '+ Watchlist'}
          </Button>
        </div>
      </div>

      <div className="grid cols-4">
        <Stat label={`${asset.symbol} last`} value={fmtCurrency(last.close)} sub={`${fmtSigned(change * 100, 2)}% vs prior close`} tone={change >= 0 ? 'pos' : 'neg'} />
        <Stat label="Regime" value={<Pill tone={regimeTone(ind.regime.regime)}>{ind.regime.regime}</Pill>} sub={ind.regime.description} />
        <Stat label="RSI(14)" value={rsiVal ? rsiVal.toFixed(1) : '-'} sub={rsiVal ? (rsiVal > 70 ? 'overbought' : rsiVal < 30 ? 'oversold' : 'neutral') : ''} />
        <Stat label="Volatility" value={volVal ? fmtPct(volVal, 0) : '-'} sub={`ATR ${fmtNumber(atrVal)}`} />
      </div>

      <Card title={`${asset.symbol} price`} subtitle="Candles with SMA-20, SMA-50, and SMA-200">
        <CandleChart
          candles={candles}
          overlays={[
            { values: ind.sma20, color: 'var(--info)' },
            { values: ind.sma50, color: 'var(--purple)' },
            { values: ind.sma200, color: 'var(--warn)' },
          ]}
          height={320}
        />
        <div className="legend" style={{ marginTop: 8 }}>
          <span><span className="swatch" style={{ background: 'var(--info)' }} /> SMA 20</span>
          <span><span className="swatch" style={{ background: 'var(--purple)' }} /> SMA 50</span>
          <span><span className="swatch" style={{ background: 'var(--warn)' }} /> SMA 200</span>
          <span><span className="swatch" style={{ background: 'var(--pos)' }} /> up candle</span>
          <span><span className="swatch" style={{ background: 'var(--neg)' }} /> down candle</span>
        </div>
      </Card>

      <div className="grid cols-3">
        <WatchAlertsCard alerts={ind.watchAlerts} />
        <Card title="Market regime" subtitle="Volatility, liquidity, trend, and drawdown checks">
          <div className="kv"><span className="k">Trend</span><span className="v"><Pill tone={trendTone[ind.regime.trend]}>{ind.regime.trend}</Pill></span></div>
          <div className="kv"><span className="k">Annual vol</span><span className="v">{fmtPct(ind.regime.volatility, 0)}</span></div>
          <div className="kv"><span className="k">Avg dollar volume</span><span className="v">{fmtCurrency(ind.regime.avgDollarVolume, 'USD', 0)}</span></div>
          <div className="kv"><span className="k">Current drawdown</span><span className="v">{fmtPct(ind.regime.drawdownPct, 1)}</span></div>
        </Card>
        <Card title="Do-not-trade checks" subtitle="Hard filters before any strategy gets attention">
          {ind.riskWarnings.length === 0 ? (
            <Banner tone="info">No hard stock risk filter is tripped for the current sample.</Banner>
          ) : (
            <div className="stack">
              {ind.riskWarnings.map((warning) => (
                <Pill key={warning.id} tone={warning.severity === 'danger' ? 'red' : 'amber'}>{warning.id}</Pill>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="RSI (14)" subtitle="Overbought above 70, oversold below 30">
          <LineChart
            height={160}
            yFormat={(n) => n.toFixed(0)}
            series={[
              { values: rsiConst(70), color: 'var(--border-2)', dashed: true },
              { values: rsiConst(30), color: 'var(--border-2)', dashed: true },
              { values: ind.rsi14, color: 'var(--accent)' },
            ]}
          />
        </Card>
        <Card title="MACD histogram" subtitle="12/26/9 momentum above/below the signal line">
          <BarChart values={ind.macd.histogram} color="auto" height={160} />
        </Card>
      </div>

      <StrategyBuilder
        asset={asset}
        strategyType={strategyType}
        setStrategyType={setStrategyType}
        fast={fast}
        setFast={setFast}
        slow={slow}
        setSlow={setSlow}
        rsiLow={rsiLow}
        setRsiLow={setRsiLow}
        rsiHigh={rsiHigh}
        setRsiHigh={setRsiHigh}
        breakoutLookback={breakoutLookback}
        setBreakoutLookback={setBreakoutLookback}
        breakoutBufferPct={breakoutBufferPct}
        setBreakoutBufferPct={setBreakoutBufferPct}
        squeezeThresholdPct={squeezeThresholdPct}
        setSqueezeThresholdPct={setSqueezeThresholdPct}
        gapThresholdPct={gapThresholdPct}
        setGapThresholdPct={setGapThresholdPct}
        useAtrStop={useAtrStop}
        setUseAtrStop={setUseAtrStop}
        atrStopMultiple={atrStopMultiple}
        setAtrStopMultiple={setAtrStopMultiple}
        fee={fee}
        setFee={setFee}
        slippage={slippage}
        setSlippage={setSlippage}
        spread={spread}
        setSpread={setSpread}
        commission={commission}
        setCommission={setCommission}
        capital={capital}
        setCapital={setCapital}
        result={strategyResult}
        onSave={saveBacktest}
      />

      <StrategyComparison comparison={comparison} />

      <TradeReplay
        asset={asset}
        result={strategyResult}
        replayIndex={replayIndex}
        setReplayIndex={setReplayIndex}
        replay={replay}
      />

      <div className="grid cols-3">
        <Card title="Signals" subtitle="Directional reads with caveats">
          <SignalRow name="Trend" dir={ind.trend.trend === 'uptrend' ? 'bullish' : ind.trend.trend === 'downtrend' ? 'bearish' : 'neutral'} note={`Normalized slope ${(ind.trend.normalizedSlope * 100).toFixed(1)}%.`} />
          <SignalRow name={ind.momentum.name} dir={ind.momentum.direction} note={ind.momentum.note} />
          <SignalRow name={ind.meanReversion.name} dir={ind.meanReversion.direction} note={ind.meanReversion.note} />
        </Card>

        <Card title="Support & resistance" subtitle="Recent swing pivots">
          <div className="uppercase faint">Resistance</div>
          {ind.supportResistance.resistances.slice(0, 3).map((r, i) => (
            <div key={i} className="kv"><span className="k">R{i + 1}</span><span className="v neg">{fmtCurrency(r)}</span></div>
          ))}
          {ind.supportResistance.resistances.length === 0 && <div className="muted">None above price.</div>}
          <div className="uppercase faint" style={{ marginTop: 10 }}>Support</div>
          {ind.supportResistance.supports.slice(0, 3).map((r, i) => (
            <div key={i} className="kv"><span className="k">S{i + 1}</span><span className="v pos">{fmtCurrency(r)}</span></div>
          ))}
          {ind.supportResistance.supports.length === 0 && <div className="muted">None below price.</div>}
        </Card>

        <Card title="Recent gaps" subtitle="Opening gaps > 2%">
          {ind.gaps.slice(-5).reverse().map((g, i) => (
            <div key={i} className="kv">
              <span className="k">{g.time}</span>
              <span className="v">
                <Pill tone={g.direction === 'up' ? 'green' : 'red'}>{g.direction} {fmtSigned(g.gapPct * 100, 1)}%</Pill>{' '}
                {g.filled ? <span className="faint">filled</span> : <span className="warn">open</span>}
              </span>
            </div>
          ))}
          {ind.gaps.length === 0 && <div className="muted">No significant gaps.</div>}
        </Card>
      </div>

      <Card title="Trade planner" subtitle="Define risk first. Position size follows from your stop, not your hopes.">
        <div className="grid cols-3">
          <div>
            <SelectField label="Direction" value={direction} onChange={(v) => setDirection(v)} options={[{ value: 'long', label: 'Long' }, { value: 'short', label: 'Short' }]} />
            <NumberField label="Entry" value={entry} onChange={setEntry} step={0.01} />
            <NumberField label="Stop" value={stop} onChange={setStop} step={0.01} />
            <NumberField label="Target" value={target} onChange={setTarget} step={0.01} />
            <NumberField label="Model probability of hitting target" value={modelProb} onChange={setModelProb} step={0.01} min={0} max={1} hint="0-1" />
          </div>

          <div className="stack">
            <div className="card tight">
              <h3>Risk / reward</h3>
              <div className="kv"><span className="k">Risk / share</span><span className="v">{fmtCurrency(rr.risk)}</span></div>
              <div className="kv"><span className="k">Reward / share</span><span className="v">{fmtCurrency(rr.reward)}</span></div>
              <div className="kv"><span className="k">R:R ratio</span><span className="v">{rr.ratio.toFixed(2)} : 1</span></div>
              <div className="kv"><span className="k">Break-even win %</span><span className="v">{fmtPct(1 / (1 + rr.ratio), 0)}</span></div>
            </div>
            <div className="card tight">
              <h3>Expected value</h3>
              <div className="kv"><span className="k">EV per $1 risked</span><span className={`v ${ev.evPerUnit >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(ev.evPerUnit, 3)}</span></div>
              <div className="kv"><span className="k">Edge vs break-even</span><span className={`v ${ev.edge >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(ev.edge * 100, 1)}%</span></div>
              <div className="kv"><span className="k">Verdict</span><span className="v">{ev.decision}</span></div>
            </div>
          </div>

          <div className="stack">
            <div className="card tight">
              <h3>Position size</h3>
              <div className="help">Risk {fmtPct(risk.maxRiskPerTradePct, 1)} of {fmtCurrency(risk.bankroll)}</div>
              <div className="kv"><span className="k">Shares</span><span className="v">{sizing.shares}</span></div>
              <div className="kv"><span className="k">Position value</span><span className="v">{fmtCurrency(sizing.positionValue)}</span></div>
              <div className="kv"><span className="k">$ at risk</span><span className="v">{fmtCurrency(sizing.dollarsAtRisk)}</span></div>
              {sizing.cappedByExposure && <Pill tone="amber">Capped by max position size</Pill>}
              {!sizing.valid && <Pill tone="red">Invalid stop</Pill>}
            </div>
            <TextArea label="Thesis (optional)" value={thesis} onChange={setThesis} placeholder="Why this trade? What invalidates it?" rows={3} />
            <div className="btn-row">
              <Button variant="primary" onClick={saveIdea} disabled={!rr.valid}>Add trade idea</Button>
              <Button onClick={openPaperPosition} disabled={!wizardSizing.valid || wizardSizing.shares <= 0}>Open paper position</Button>
            </div>
          </div>
        </div>
      </Card>

      <PositionSizingWizard
        entry={entry}
        direction={direction}
        atrVal={atrVal}
        riskPct={wizardRiskPct}
        setRiskPct={setWizardRiskPct}
        maxPct={wizardMaxPct}
        setMaxPct={setWizardMaxPct}
        atrMultiple={wizardAtrMultiple}
        setAtrMultiple={setWizardAtrMultiple}
        stop={wizardStop}
        sizing={wizardSizing}
      />

      <PaperLedger rows={stockLedger} />
    </div>
  )
}

function StrategyBuilder(props: {
  asset: Asset
  strategyType: StockStrategyType
  setStrategyType: (value: StockStrategyType) => void
  fast: number
  setFast: (value: number) => void
  slow: number
  setSlow: (value: number) => void
  rsiLow: number
  setRsiLow: (value: number) => void
  rsiHigh: number
  setRsiHigh: (value: number) => void
  breakoutLookback: number
  setBreakoutLookback: (value: number) => void
  breakoutBufferPct: number
  setBreakoutBufferPct: (value: number) => void
  squeezeThresholdPct: number
  setSqueezeThresholdPct: (value: number) => void
  gapThresholdPct: number
  setGapThresholdPct: (value: number) => void
  useAtrStop: boolean
  setUseAtrStop: (value: boolean) => void
  atrStopMultiple: number
  setAtrStopMultiple: (value: number) => void
  fee: number
  setFee: (value: number) => void
  slippage: number
  setSlippage: (value: number) => void
  spread: number
  setSpread: (value: number) => void
  commission: number
  setCommission: (value: number) => void
  capital: number
  setCapital: (value: number) => void
  result: StockBacktestResult
  onSave: () => void
}) {
  return (
    <Card title="Strategy builder" subtitle="Build and test stock strategies with costs, slippage, commissions, and optional ATR stops.">
      <div className="grid cols-4">
        <SelectField label="Strategy" value={props.strategyType} onChange={props.setStrategyType} options={STRATEGY_OPTIONS} />
        <NumberField label="Fast / short period" value={props.fast} onChange={(v) => props.setFast(Math.max(2, Math.round(v)))} />
        <NumberField label="Slow / long period" value={props.slow} onChange={(v) => props.setSlow(Math.max(3, Math.round(v)))} />
        <NumberField label="Capital" value={props.capital} onChange={props.setCapital} step={1000} />
      </div>
      <div className="grid cols-4">
        <NumberField label="RSI low" value={props.rsiLow} onChange={props.setRsiLow} />
        <NumberField label="RSI high" value={props.rsiHigh} onChange={props.setRsiHigh} />
        <NumberField label="Breakout lookback" value={props.breakoutLookback} onChange={(v) => props.setBreakoutLookback(Math.max(2, Math.round(v)))} />
        <NumberField label="Breakout buffer" value={props.breakoutBufferPct} onChange={props.setBreakoutBufferPct} step={0.0005} />
        <NumberField label="Squeeze width" value={props.squeezeThresholdPct} onChange={props.setSqueezeThresholdPct} step={0.005} />
        <NumberField label="Gap threshold" value={props.gapThresholdPct} onChange={props.setGapThresholdPct} step={0.005} />
        <NumberField label="ATR stop multiple" value={props.atrStopMultiple} onChange={props.setAtrStopMultiple} step={0.25} min={0.5} />
        <Toggle label="ATR stop system" checked={props.useAtrStop} onChange={props.setUseAtrStop} />
      </div>
      <div className="grid cols-4">
        <NumberField label="Fee %" value={props.fee} onChange={props.setFee} step={0.0005} />
        <NumberField label="Slippage %" value={props.slippage} onChange={props.setSlippage} step={0.0005} />
        <NumberField label="Spread %" value={props.spread} onChange={props.setSpread} step={0.0005} />
        <NumberField label="Commission / trade" value={props.commission} onChange={props.setCommission} step={0.5} min={0} />
      </div>
      <div className="grid cols-4">
        <Stat label="Return" value={fmtPctValue(props.result.metrics.totalReturnPct)} tone={props.result.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'} />
        <Stat label="Max drawdown" value={fmtPctValue(props.result.metrics.maxDrawdownPct)} tone="neg" />
        <Stat label="Trades" value={props.result.metrics.trades} sub={`${props.result.stopouts} ATR stops`} />
        <Stat label="Profit factor" value={Number.isFinite(props.result.metrics.profitFactor) ? props.result.metrics.profitFactor.toFixed(2) : 'inf'} />
      </div>
      {props.result.overfitWarning && <Banner tone="warn">{props.result.overfitWarning}</Banner>}
      <LineChart series={[{ values: props.result.equityCurve, color: 'var(--accent)', fill: true }]} height={180} yFormat={(n) => `$${n.toFixed(0)}`} />
      <div className="btn-row" style={{ marginTop: 8 }}>
        <Button onClick={props.onSave}>Save backtest result</Button>
      </div>
    </Card>
  )
}

function StrategyComparison({ comparison }: { comparison: Array<{ type: StockStrategyType; result: StockBacktestResult }> }) {
  return (
    <Card title="Strategy comparison dashboard" subtitle="Same asset, same costs, default parameters for each strategy family.">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Strategy</th><th>Return</th><th>Max DD</th><th>Sharpe-like</th><th>Win rate</th><th>Trades</th><th>Stopouts</th><th>Warning</th></tr>
          </thead>
          <tbody>
            {comparison.map(({ type, result }) => (
              <tr key={type}>
                <td>{strategyLabel(type)}</td>
                <td className={`mono ${result.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(result.metrics.totalReturnPct, 1)}%</td>
                <td className="mono neg">{result.metrics.maxDrawdownPct.toFixed(1)}%</td>
                <td className="mono">{result.metrics.sharpeLike.toFixed(2)}</td>
                <td className="mono">{fmtPct(result.metrics.winRate, 0)}</td>
                <td className="mono">{result.metrics.trades}</td>
                <td className="mono">{result.stopouts}</td>
                <td>{result.overfitWarning ? <Pill tone="amber">review</Pill> : <Pill tone="green">clear</Pill>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function TradeReplay({
  asset,
  result,
  replayIndex,
  setReplayIndex,
  replay,
}: {
  asset: Asset
  result: StockBacktestResult
  replayIndex: number
  setReplayIndex: (value: number) => void
  replay: { signal: number; equity: number; candle: Asset['candles'][number] }
}) {
  return (
    <Card title="Trade replay mode" subtitle="Step through the backtest bar by bar and inspect the signal that would have been active.">
      <RangeField
        label="Replay bar"
        min={0}
        max={Math.max(0, asset.candles.length - 1)}
        step={1}
        value={replayIndex}
        onChange={(v) => setReplayIndex(Math.round(v))}
        display={`${replay.candle.time} / ${replayIndex + 1} of ${asset.candles.length}`}
      />
      <div className="grid cols-4">
        <Stat label="Signal" value={<Pill tone={signalTone[replay.signal]}>{signalName(replay.signal)}</Pill>} />
        <Stat label="Close" value={fmtCurrency(replay.candle.close)} />
        <Stat label="Volume" value={fmtNumber(replay.candle.volume, 0)} />
        <Stat label="Equity" value={fmtCurrency(replay.equity)} tone={replay.equity >= result.equityCurve[0] ? 'pos' : 'neg'} />
      </div>
    </Card>
  )
}

function PositionSizingWizard(props: {
  entry: number
  direction: Direction
  atrVal: number
  riskPct: number
  setRiskPct: (value: number) => void
  maxPct: number
  setMaxPct: (value: number) => void
  atrMultiple: number
  setAtrMultiple: (value: number) => void
  stop: number
  sizing: ReturnType<typeof positionSize>
}) {
  return (
    <Card title="Position-sizing wizard" subtitle="ATR stop first, then shares. This keeps sizing tied to current volatility.">
      <div className="grid cols-4">
        <NumberField label="Risk per trade" value={props.riskPct} onChange={props.setRiskPct} step={0.0025} min={0} max={0.25} />
        <NumberField label="Max position" value={props.maxPct} onChange={props.setMaxPct} step={0.01} min={0} max={1} />
        <NumberField label="ATR multiple" value={props.atrMultiple} onChange={props.setAtrMultiple} step={0.25} min={0.5} />
        <div className="card tight">
          <div className="kv"><span className="k">ATR</span><span className="v">{fmtCurrency(props.atrVal)}</span></div>
          <div className="kv"><span className="k">ATR stop</span><span className="v">{fmtCurrency(props.stop)}</span></div>
        </div>
      </div>
      <div className="grid cols-4">
        <Stat label="Shares" value={props.sizing.shares} />
        <Stat label="Position value" value={fmtCurrency(props.sizing.positionValue)} />
        <Stat label="Dollars at risk" value={fmtCurrency(props.sizing.dollarsAtRisk)} />
        <Stat label="Bankroll used" value={fmtPct(props.sizing.pctOfBankroll, 1)} />
      </div>
    </Card>
  )
}

function PaperLedger({ rows }: { rows: Array<{ id: string; type: string; label: string; status: string; stake: number; pnl?: number }> }) {
  return (
    <Card title="Paper-trading ledger" subtitle="Open paper positions and stock trade ideas for the selected symbol.">
      {rows.length === 0 ? (
        <EmptyState>No paper stock entries for this symbol yet.</EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Type</th><th>Label</th><th>Status</th><th>Stake</th><th>P/L</th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td><Pill tone={row.type === 'position' ? 'blue' : 'purple'}>{row.type}</Pill></td>
                  <td>{row.label}</td>
                  <td>{row.status}</td>
                  <td className="mono">{fmtCurrency(row.stake)}</td>
                  <td className={`mono ${(row.pnl ?? 0) >= 0 ? 'pos' : 'neg'}`}>{row.pnl === undefined ? '-' : fmtSigned(row.pnl, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function WatchAlertsCard({ alerts }: { alerts: StockWatchAlert[] }) {
  return (
    <Card title="Watchlist alerts" subtitle="Price, volume, RSI, gap, and moving-average triggers.">
      {alerts.length === 0 ? (
        <EmptyState>No active watch alerts.</EmptyState>
      ) : (
        <div className="stack">
          {alerts.map((alert) => (
            <Pill key={alert.id} tone={alert.tone}>{alert.message}</Pill>
          ))}
        </div>
      )}
    </Card>
  )
}

function SignalRow({ name, dir, note }: { name: string; dir: string; note: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="row between">
        <strong>{name}</strong>
        <Pill tone={sigTone[dir]}>{dir}</Pill>
      </div>
      <div className="muted" style={{ fontSize: 12 }}>{note}</div>
    </div>
  )
}

function buildComparisons(asset: Asset, costs: CostModel, capital: number): Array<{ type: StockStrategyType; result: StockBacktestResult }> {
  return STRATEGY_OPTIONS.filter((option) => option.value !== 'momentum').map((option) => ({
    type: option.value,
    result: backtestStock(asset.candles, { ...DEFAULT_STOCK_PARAMS, type: option.value }, costs, capital),
  }))
}

function buildReplay(asset: Asset, result: StockBacktestResult, replayIndex: number) {
  const idx = Math.max(0, Math.min(asset.candles.length - 1, replayIndex))
  return {
    signal: result.signals[idx] ?? 0,
    equity: result.equityCurve[idx] ?? result.equityCurve[result.equityCurve.length - 1] ?? 0,
    candle: asset.candles[idx],
  }
}

function buildStockLedger(asset: Asset, ideas: TradeIdea[], positions: Position[]) {
  return [
    ...positions
      .filter((p) => p.kind === 'stock' && p.label.toUpperCase().includes(asset.symbol))
      .map((p) => ({ id: p.id, type: 'position', label: p.label, status: p.status, stake: p.stake, pnl: p.realizedPnl ?? (p.currentPrice - p.entryPrice) * p.size * (p.direction === 'long' ? 1 : -1) })),
    ...ideas
      .filter((idea) => idea.assetId === asset.id)
      .map((idea) => ({ id: idea.id, type: 'idea', label: `${idea.symbol} ${idea.direction}`, status: idea.status, stake: Math.abs(idea.entry - idea.stop), pnl: undefined })),
  ]
}

function strategyLabel(type: StockStrategyType): string {
  return STRATEGY_OPTIONS.find((option) => option.value === type)?.label ?? type
}

function signalName(signal: number): string {
  if (signal > 0) return 'LONG'
  if (signal < 0) return 'SHORT'
  return 'FLAT'
}

function regimeTone(regime: string): Tone {
  if (regime === 'bull-trend') return 'green'
  if (regime === 'bear-trend' || regime === 'deep-drawdown' || regime === 'high-volatility' || regime === 'low-liquidity') return 'red'
  return 'amber'
}

function round2(v: number): number {
  return Math.round(v * 100) / 100
}
